import { debounce } from 'lodash';
import { ChangeEvent, FC, useMemo, useRef } from 'react';

import { SliderWithTicks } from 'src/general/components/SliderWithTicks/SliderWithTicks';
import { InputWithButton } from 'src/general/components/forms/Input/InputWithButton';

type Props = {
  minRate: number;
  maxRate: number;
  'data-testid'?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onTrackingEvent?: (value: any) => void;
};

export const FinanceCalculatorSlider: FC<Props> = ({
  minRate,
  maxRate,
  'data-testid': dataTestId,
  value,
  onChange,
  onTrackingEvent,
}) => {
  const debounceRef = useRef(
    debounce((value) => {
      onTrackingEvent?.(value);
    }, 500),
  );

  const [ratesArray, marks, minValue, maxValue] = useMemo(() => {
    const ratesArray = Array.from(
      { length: Math.ceil(maxRate) - Math.floor(minRate) + 1 },
      (_, i) => i + Math.floor(minRate),
    );
    const marks: number[] = [...new Set(ratesArray)].sort((a, b) => a - b);
    const availableRates = marks.length !== 0;
    const minValue = availableRates ? marks[0] : undefined;
    const maxValue = availableRates ? marks.at(-1) : undefined;

    return [ratesArray, marks, minValue, maxValue];
  }, [maxRate, minRate]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    debounceRef.current(e.target.value);
  };

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-extrabold text-tex-def-tie1">Interest rate</div>
          <div className="c-fs-caption font-normal text-tex-def-tie2">
            Slide between {minValue}% and {maxValue}%
          </div>
        </div>
        <div className="w-[110px]">
          <InputWithButton
            id="interestRate"
            name="interestRate"
            labelOutside
            type="number"
            inputMode="numeric"
            value={value}
            buttonRight="%"
            min={minValue}
            max={maxValue}
            step={0.01}
            inputClassName="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
            data-testid={dataTestId}
            onChange={handleOnChange}
          />
        </div>
      </div>
      <SliderWithTicks
        value={value}
        min={minValue}
        max={maxValue}
        step={0.01}
        onChange={handleOnChange}
        disabled={!ratesArray.length}
        ariaLabel="Interest rate"
        marks={marks}
      />
    </div>
  );
};
